/**
 * E98.F7.T02 — o envelope e a biometria do provedor no resumo do protocolo.
 *
 * O certificado de conclusão do envelope e o relatório da biometria (E98.F2) dão ao protocolo
 * o que a tela ainda não mostrava: os signatários por papel (o cliente e o backoffice do banco,
 * que nunca vira dado do cliente), a autenticação de acesso, o fuso presumido declarado, os
 * documentos e as páginas que o envelope declara, e, da biometria, o provedor, o processo, a
 * prova de vida declarada e a coleta por reaproveitamento. Função pura: lê, não conclui.
 */
import { ptBR } from '@/i18n/pt-BR';
import type {
  BiometriaProtocoloData,
  EnvelopeDoProtocoloData,
  SignatarioDoEnvelopeData,
} from '@/lib/exame_eletronico_protocolo';

const t = ptBR.exameEletronico.resumoEnvelope;

const FORMATO = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: 'America/Sao_Paulo',
});

function brasilia(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const data = new Date(iso);
  return Number.isNaN(data.getTime()) ? iso : `${FORMATO.format(data)} (Brasília)`;
}

/** As linhas da biometria que o relatório do provedor declara, além da presença. */
export function linhasDaBiometriaDoProvedor(bio: BiometriaProtocoloData): string[] {
  const linhas: string[] = [];
  if (bio.fornecedor) linhas.push(t.provedor.replace('{valor}', bio.fornecedor));
  if (bio.prova_de_vida === true) linhas.push(t.provaDeVidaDeclarada);
  if (bio.prova_de_vida === false) linhas.push(t.provaDeVidaNegada);
  if (bio.modulo_de_coleta) linhas.push(t.moduloDeColeta.replace('{valor}', bio.modulo_de_coleta));
  if (bio.coleta_por_reaproveitamento === true) linhas.push(t.coletaPorReaproveitamento);
  if (bio.documento_validado === true) linhas.push(t.documentoValidadoDeclarado);
  return linhas;
}

function linhaDoSignatario(signatario: SignatarioDoEnvelopeData): string {
  const partes = [t.papel[signatario.papel].replace('{nome}', signatario.nome ?? t.semNome)];
  const assinou = brasilia(signatario.assinado_em);
  if (assinou !== null) partes.push(t.assinouEm.replace('{quando}', assinou));
  if (signatario.autenticacao) partes.push(signatario.autenticacao);
  if (signatario.ip) partes.push(t.ip.replace('{valor}', signatario.ip));
  return partes.join(' · ');
}

/** A coluna do envelope; vazia quando o protocolo não o traz. */
export function linhasDoEnvelope(envelope: EnvelopeDoProtocoloData | null | undefined): string[] {
  if (!envelope) return [];
  const ordenados = [...envelope.signatarios].sort((a, b) => (a.ordem ?? 99) - (b.ordem ?? 99));
  const linhas = ordenados.map(linhaDoSignatario);
  if (envelope.documentos.length > 0) {
    linhas.push(t.documentos.replace('{n}', String(envelope.documentos.length)));
  }
  if (envelope.paginas_declaradas != null) {
    linhas.push(t.paginas.replace('{n}', String(envelope.paginas_declaradas)));
  }
  if (envelope.fuso_presumido)
    linhas.push(t.fusoPresumido.replace('{valor}', envelope.fuso_presumido));
  else if (envelope.fuso_declarado)
    linhas.push(t.fusoDeclarado.replace('{valor}', envelope.fuso_declarado));
  return linhas;
}
